import { useCallback, useEffect, useRef, useState } from 'react';

import SDK from '../../../SDK';
import logger from '../../../../Helpers/logger';
import { isAppOnline } from '../../../../Helpers/Utility';
import { showOfflineError } from '../../../../ui/DialogFrame/dialogToast';
import { CONTACT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from './constants';

/**
 * Contact list search: the keyword applies once typing stops or on Enter, spaces around it
 * are cut, and pages of 10 are appended on demand.
 *
 * @param {boolean} isActive Loads only while the pop-up using it is open.
 * @param {number} debounceMs Delay after the last keystroke; FR60 waits 3s.
 * @returns {object} inputValue, keyword, contacts, isLoading, hasMore and the handlers.
 */
export default function useContactSearch(isActive, debounceMs = SEARCH_DEBOUNCE_MS) {
    const [inputValue, setInputValue] = useState('');
    const [keyword, setKeyword] = useState('');
    const [contacts, setContacts] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const requestIdRef = useRef(0);
    const timerRef = useRef(null);

    const fetchPage = useCallback(async (searchKeyword, pageNumber) => {
        if (!isAppOnline()) {
            showOfflineError();
            return;
        }
        // Only the latest request may write, so a slow older search cannot win.
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        const response = await SDK.getContactList(searchKeyword, pageNumber, CONTACT_PAGE_SIZE);
        if (requestId !== requestIdRef.current) return;

        setIsLoading(false);
        setHasLoaded(true);
        if (response?.statusCode !== 200) {
            logger.error('Contact list failed', { statusCode: response?.statusCode, page: pageNumber });
            return;
        }
        const { contacts: pageContacts = [], totalPages: pages = 0 } = response.data || {};
        setContacts(previous => (pageNumber === 1 ? pageContacts : [...previous, ...pageContacts]));
        setPage(pageNumber);
        setTotalPages(pages);
    }, []);

    useEffect(() => {
        if (isActive) fetchPage(keyword, 1);
    }, [isActive, keyword, fetchPage]);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const applyKeyword = useCallback(value => {
        clearTimeout(timerRef.current);
        setKeyword(value.trim());
    }, []);

    const handleInputChange = useCallback(
        value => {
            setInputValue(value);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => applyKeyword(value), debounceMs);
        },
        [applyKeyword, debounceMs],
    );

    // Keeps the loaded rows, so an offline reopen still has a list.
    const reset = useCallback(() => {
        clearTimeout(timerRef.current);
        setInputValue('');
        setKeyword('');
    }, []);

    const handleSubmit = useCallback(() => applyKeyword(inputValue), [applyKeyword, inputValue]);

    const hasMore = page < totalPages;

    const handleLoadMore = useCallback(() => {
        if (!isLoading && hasMore) fetchPage(keyword, page + 1);
    }, [fetchPage, hasMore, isLoading, keyword, page]);

    const reload = useCallback(() => fetchPage(keyword, 1), [fetchPage, keyword]);

    return {
        inputValue,
        keyword,
        contacts,
        isLoading,
        hasLoaded,
        hasMore,
        onInputChange: handleInputChange,
        onSubmit: handleSubmit,
        onLoadMore: handleLoadMore,
        reload,
        reset,
    };
}
